import {MinimalFolder} from 'constants/FolderConstants';
import {REGISTRY_TYPE} from 'constants/SourceConstants';
import {foldersToSelectTreeItems} from 'file-browser/FileSelectorUtil';
import type {FilterTreeNode} from 'omnibox/omniboxFilterTree';
import {sortedRootItems} from 'ui-library/select-tree/SelectTreeUtil';

/**
 * Builds the Folder tree from the folders the sidepanel folder filter loads, keeping the
 * same visibility rules (registries and hidden or archived projects are left out) and the same root
 * order. Nodes are keyed by folder apiId; a project's root folder selects the project itself.
 */
export function buildFolderTreeNodes(folders: ReadonlyArray<MinimalFolder>): Array<FilterTreeNode> {
  const selectable = folders.filter(
    ({source}) =>
      source != null &&
      source.sourceType !== REGISTRY_TYPE &&
      !source.hidden &&
      !source.archived &&
      !source.archiveRecord
  );
  const foldersById = new Map(selectable.map((folder) => [folder.api_identifier, folder]));
  const items = foldersToSelectTreeItems(selectable);
  const nodes = new Map(
    items.map((item): [string, FilterTreeNode] => {
      const folder = foldersById.get(item.id)!;
      const base = {id: item.id, parentId: item.parentId, name: item.name};
      if (folder.parent == null) {
        return [
          item.id,
          {
            ...base,
            icon: 'source',
            filter: {kind: 'project', id: folder.source!.id, name: folder.name},
          },
        ];
      }
      let root = folder;
      while (root.parent != null) {
        root = root.parent;
      }
      return [
        item.id,
        {
          ...base,
          icon: 'folder',
          filter: {kind: 'folder', id: folder.api_identifier, name: folder.name, projectName: root.name},
        },
      ];
    })
  );
  // Roots go first in the sidepanel's root order; children keep the server's order beneath them.
  const isRoot = (parentId: string | null): boolean => parentId == null || !nodes.has(parentId);
  const roots = sortedRootItems(items.filter((item) => isRoot(item.parentId)));
  const children = items.filter((item) => !isRoot(item.parentId));
  return [...roots, ...children].map((item) => nodes.get(item.id)!);
}
